import { Box, styled, Typography, ToggleButtonGroup, ToggleButton, GlobalStyles } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  GetCertificatesForRelacementQueryVariables,
  useGetCertificatesForRelacementQuery,
} from "graphqlBase/certificates/__generated__/getCertificatesForRelacement";
import { useGetUserInfo } from "lib/userInfo";
import { useDebounce } from "pages/CertificationManagement/CustomDataGridFilters/utils/useCustomFilterbar";
import switchStatus from "pages/CertificationManagement/UnitsOverview/StatusColumn/switchStatus";
import { useMemo, useState } from "react";
import { translations } from "translations";
interface ReplaceCertificateSelectProps {
  setCertificateId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setAlignment: React.Dispatch<React.SetStateAction<"extend" | "withdraw">>;
  alignment: "extend" | "withdraw";
  onCertificateSelect?: (certificateFormType?: string | null) => void;
}

//@Todo: find type
interface Option {
  label: string;
  revision: number | null | undefined;
  value: string;
  status: string;
  certificateFormType?: string | null;
}

export const OptionLabelSC = styled(Typography, {
  label: "OptionLabel",
  shouldForwardProp(propName) {
    return propName !== "status";
  },
})(() => ({
  width: "50%",
  marginRight: "1rem",
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "status",
})(() => ({
  "& .MuiAutocomplete-inputRoot": {
    borderRadius: "0px",
  },
}));

const StyledAutocomplete = styled(Autocomplete<Option>)(() => ({
  width: "100%",
  borderRadius: "0px",
}));

const ReplaceCertificateSelect: React.FC<ReplaceCertificateSelectProps> = ({
  setCertificateId,
  alignment,
  setAlignment,
  onCertificateSelect,
}) => {
  const userInfo = useGetUserInfo();
  const [search, setSearch] = useState("");
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const handleToggleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: "withdraw" | "extend") => {
    // Prevent deselection - always keep one option selected
    if (newAlignment !== null) {
      setAlignment(newAlignment);
      setSearch("");
      setSelectedOption(null);
      setCertificateId(undefined);
    }
  };

  const searchDebounced = useDebounce(search, 500);

  const variables: GetCertificatesForRelacementQueryVariables = useMemo(() => {
    const tenantFilter = {
      or: [
        {
          certificateAuthorityId: {
            eq: userInfo?.tenantId ?? "",
          },
        },
        {
          certificateHolderId: {
            eq: userInfo?.tenantId ?? "",
          },
        },
      ],
    };

    return {
      where: {
        certificateNumber: {
          contains: searchDebounced,
        },
        ...(alignment === "extend" && {
          certificateValidityStatusId: {
            eq: "c47a0b7c-701b-5544-92b0-6bcb48aac633", // Valid / Ongoing
          },
        }),
        ...(alignment === "withdraw" && {
          certificateValidityStatusId: {
            in: [
              "c47a0b7c-701b-5544-92b0-6bcb48aac633",
              "02ab294a-cfe3-57d0-8831-2d600c44397c",
              "63a507c8-7124-5eec-8510-02b74a3a8dca",
            ], // Valid / Ongoing, valid(replaceBy), expirec
          },
        }),
        certificateBridgeCertificateNormIssueDates: {
          none: {
            certificateNormIssueDate: {
              certificateNormBridges: {
                some: {
                  certificateNorm: { certificateNormName: { contains: "4105" } },
                },
              },
            },
          },
        },
        ...tenantFilter,
      },
      order: [
        {
          certificateNumber: "ASC",
          revision: "DESC",
        },
      ],
    };
  }, [searchDebounced, userInfo, alignment]);

  const { data } = useGetCertificatesForRelacementQuery({
    variables,
    skip: !searchDebounced || !searchDebounced.length,
    fetchPolicy: "network-only",
  });

  const options: Option[] = useMemo(() => {
    return (data?.certificates?.items ?? []).map((item) => ({
      label: item.certificateNumber,
      revision: item.revision,
      value: item.id,
      status: item.certificateValidityStatus.certificateValidityStatusName,
      certificateFormType: item.certificateFormType,
    }));
  }, [data]);

  return (
    <>
      <GlobalStyles
        styles={{
          ".MuiAutocomplete-paper": {
            overflow: "visible !important",
          },
          ".MuiAutocomplete-listbox": {
            maxHeight: "300px !important",
            overflowY: "auto",
          },
          ".MuiAutocomplete-option": {
            minHeight: "60px",
            alignItems: "center",
            padding: "8px 16px",
            display: "flex",
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "2rem",
          flex: "1 0 0",
        }}
      >
        <ToggleButtonGroup
          color="primary"
          value={alignment}
          exclusive
          onChange={handleToggleChange}
          aria-label="set replacement type"
        >
          <ToggleButton value="withdraw">
            {translations.pages.certificateUpload.certificateUploadProcess.withdrawCertificateOption}
          </ToggleButton>
          <ToggleButton value="extend">
            {translations.pages.certificateUpload.certificateUploadProcess.extendValidityOption}
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body1">
          {translations.pages.certificateUpload.certificateUploadProcess.replaceCertificatePropmt}
        </Typography>
        <StyledAutocomplete
          disablePortal
          value={selectedOption}
          onChange={(event, newValue) => {
            if (alignment === "extend") {
              const selectedCertificate = data?.certificates?.items?.find((item) => item.id === newValue?.value);
              // Get the certificate chain info and use the last item (most recent) in the chain
              const certificateChainInfo = selectedCertificate?.certificateChainInfo;

              if (certificateChainInfo && certificateChainInfo.length > 0) {
                // Sort by revisionNumber in ascending order and get the last item (highest revisionNumber)
                const sortedChain = [...certificateChainInfo].sort((a, b) => (a && b ? a.revision - b.revision : 0));

                const chainElementIdForUpdate = sortedChain[sortedChain.length - 1]?.certificateId;

                if (chainElementIdForUpdate) {
                  setSelectedOption(newValue);
                  setCertificateId(chainElementIdForUpdate);
                  // Call the callback with the certificate form type
                  onCertificateSelect?.(newValue?.certificateFormType);
                  return;
                }
              }
            }
            setSelectedOption(newValue);
            if (newValue?.value) {
              setCertificateId(newValue.value);
              // Call the callback with the certificate form type
              onCertificateSelect?.(newValue?.certificateFormType);
            }
          }}
          inputValue={search}
          onInputChange={(event, newInputValue) => {
            setSearch(newInputValue);
          }}
          noOptionsText={translations.pages.certificateUpload.noOptionsText}
          id="combo-box-demo"
          options={options}
          renderInput={(params) => <StyledTextField {...params} />}
          renderOption={(props, option) => (
            <li {...props}>
              <OptionLabelSC>{option.label}</OptionLabelSC>{" "}
              <OptionLabelSC>
                {translations.entities.certificate.revision}: {option.revision ?? 0}
              </OptionLabelSC>
              {switchStatus(option)}
            </li>
          )}
        />
      </Box>
    </>
  );
};
export default ReplaceCertificateSelect;
